import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { Star, Shield, Users, Eye, ChevronRight, Scissors, Sparkles } from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-stone-900">BeautyPro Review</span>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="h-10 px-6 rounded-full bg-stone-900 text-white hover:bg-stone-800" data-testid="dashboard-btn">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="h-10 px-6 rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100" data-testid="login-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="h-10 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]" data-testid="register-btn">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg" 
            alt="Beauty Salon" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FDFCF8] via-[#FDFCF8]/90 to-[#FDFCF8]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <p className="text-[#D97757] font-medium text-sm tracking-wide uppercase mb-4">For Licensed Beauty Professionals</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-stone-900 tracking-tight leading-tight mb-6">
                Know Your Clients <span className="italic text-[#D97757]">Before</span> They Sit Down
              </h1>
              <p className="text-stone-600 text-lg leading-relaxed mb-8 max-w-lg">
                A private community for licensed cosmetologists, barbers, nail technicians, and estheticians to share client experiences. Make informed decisions and protect your time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="h-12 px-8 rounded-full bg-stone-900 text-white hover:bg-stone-800 text-base font-medium" data-testid="hero-get-started-btn">
                    Start Your Free 60 Day Trial <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="h-12 px-8 rounded-full border-stone-300 text-stone-700 hover:bg-stone-100 text-base font-medium" data-testid="hero-signin-btn">
                    Sign In
                  </Button>
                </Link>
              </div>
              
              {/* License Types */}
              <div className="mt-10 flex flex-wrap gap-3">
                {['Cosmetology', 'Barber', 'Nail Tech', 'Esthetician'].map((license) => (
                  <span key={license} className="px-4 py-2 rounded-full bg-stone-100 text-stone-700 text-sm font-medium">
                    {license} Licensed
                  </span>
                ))}
              </div>
            </div>
            
            <div className="hidden lg:block animate-fade-in">
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg" 
                  alt="Beauty Professional" 
                  className="rounded-2xl shadow-2xl object-cover w-full h-[500px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-stone-900 mb-4">
              Built for All Beauty Professionals
            </h2>
            <p className="text-stone-600 text-lg">Licensed professionals protecting each other</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Scissors,
                title: "Hair Stylists",
                description: "Cosmetology license holders - stylists and colorists"
              },
              {
                icon: Scissors,
                title: "Barbers",
                description: "Barber license holders - fades, cuts, mens grooming"
              },
              {
                icon: Sparkles,
                title: "Nail Technicians", 
                description: "Manicurists, pedicurists, and nail artists"
              },
              {
                icon: Star,
                title: "Estheticians",
                description: "Skincare specialists, facial experts, waxing professionals"
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-[#FDFCF8]">
                <div className="w-14 h-14 rounded-full bg-[#D97757]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#D97757]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">{item.title}</h3>
                <p className="text-stone-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#FDFCF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 mb-4">
              Protect Your Business, Help Your Community
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto">
              Everything you need to make informed decisions about your clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Verified Professionals",
                description: "Only licensed cosmetologists, barbers, nail techs, and estheticians can join. Upload your credentials and join instantly."
              },
              {
                icon: Eye,
                title: "Anonymous Reviews",
                description: "Share honest feedback anonymously. Your identity is protected while helping the community."
              },
              {
                icon: Users,
                title: "Detailed Ratings",
                description: "Rate clients on service quality, punctuality, payment behavior, and communication."
              }
            ].map((feature, index) => (
              <Card key={index} className={`bg-white border-stone-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-slide-up stagger-${index + 1}`} data-testid={`feature-card-${index}`}>
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-[#D97757]/10 flex items-center justify-center mb-6">
                    <feature.icon className="w-6 h-6 text-[#D97757]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">{feature.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Free Access Section */}
      <section className="py-20 bg-white" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 mb-4">
              Free Access for Professionals
            </h2>
            <p className="text-stone-600 text-lg">Join our community of verified beauty professionals</p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            {/* Free Plan Card */}
            <Card className="bg-white border-2 border-[#5F8D4E] shadow-xl overflow-hidden" data-testid="pricing-free-card">
              <div className="bg-[#5F8D4E] py-2 text-center">
                <span className="text-white text-sm font-medium">Limited Time - Free Access</span>
              </div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#5F8D4E]/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#5F8D4E]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-stone-900">Pro Access</h3>
                </div>
                <p className="text-stone-600 mb-4">Full access for all licensed beauty professionals</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#5F8D4E]">FREE</span>
                  <span className="text-stone-600 ml-2 line-through">$12.99/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    "Unlimited client searches",
                    "Submit unlimited reviews",
                    "View all community reviews",
                    "Anonymous review option",
                    "Referral program access",
                    "Real-time notifications"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-stone-700 text-sm">
                      <div className="w-5 h-5 rounded-full bg-[#5F8D4E]/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-[#5F8D4E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full h-11 rounded-full bg-[#5F8D4E] text-white hover:bg-[#4A7340] font-medium" data-testid="pricing-free-btn">
                    Get Free Access
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-center text-stone-500 text-sm mt-8">Requires valid professional license verification</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-stone-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Join our Network of Beauty Professionals Today
          </h2>
          <p className="text-stone-400 text-lg mb-8 max-w-2xl mx-auto">
            Hair stylists, barbers, nail techs, and estheticians trust us. Stop guessing about new clients.
          </p>
          <Link to="/register">
            <Button className="h-12 px-8 rounded-full bg-[#D97757] text-white hover:bg-[#C06040] text-base font-medium" data-testid="cta-register-btn">
              Get Started Now <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900 border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-white">BeautyPro Review</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-4">
                <Link to="/terms" className="text-stone-400 hover:text-white text-sm transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-stone-400 hover:text-white text-sm transition-colors">
                  Privacy Policy
                </Link>
              </div>
              <a 
                href="mailto:info@beautyproreview.com" 
                className="text-stone-400 hover:text-white text-sm transition-colors"
              >
                info@beautyproreview.com
              </a>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-stone-800 text-center">
            <p className="text-stone-500 text-sm">
              © {new Date().getFullYear()} BeautyPro Review. For licensed beauty professionals only.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
